import {
  Action,
  Card,
  Icon,
  RelativeDateTime,
  TextInput,
  Disclosure,
  DisclosureChevron,
  Feed,
} from 'corso-admin-ui';
import { CrewMerchantUi } from 'corso-types';
import { formatter } from 'corso-web-lib';
import { ReactNode, useState } from 'react';
import { match } from 'ts-pattern';
import { useCurrency } from '~/hooks/useCurrency';
import { useUser } from '~/providers/UserProvider';
import { timelineEventIcons } from './timelineEventIcons';

const getBestSubject = (event: CrewMerchantUi.TimelineEvent) => {
  // ? prefer the user with first and last name over legacy subject
  if (event.user?.firstName && event.user.lastName)
    return `${event.user.firstName} ${event.user.lastName}`;
  return event.subject;
};

function EventContent({
  event,
  trailing,
}: {
  event: CrewMerchantUi.TimelineEvent;
  trailing?: ReactNode;
}) {
  const subject = getBestSubject(event);
  return (
    <div className="flex-auto text-xs">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1">
          {subject ? (
            <p>
              <span className="font-medium text-neutral-800">{subject}</span>{' '}
              <span className="text-neutral-500">
                {event.message.charAt(0).toLowerCase()}
                {event.message.slice(1)}
              </span>
            </p>
          ) : (
            <p className="text-neutral-800">{event.message}</p>
          )}
          {trailing}
        </div>

        <RelativeDateTime
          dateTime={event.datetime}
          className="flex-none py-0.5 text-xs leading-5 text-neutral-500"
        />
      </div>
    </div>
  );
}

function MetaItem({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div>
      <dt className="font-medium text-neutral-800">{label}</dt>
      <dd className="text-neutral-500">{value}</dd>
    </div>
  );
}

function EventContentWithMeta({
  event,
  meta,
}: {
  event: CrewMerchantUi.TimelineEvent;
  meta: NonNullable<CrewMerchantUi.TimelineEvent['meta']>;
}) {
  const { currencyCode } = useCurrency();
  return (
    <Disclosure
      defaultOpen={event.type === 'warning'}
      renderSummary={
        <EventContent event={event} trailing={<DisclosureChevron />} />
      }
    >
      <dl className="mt-1 flex flex-col gap-1.5 text-xs">
        {meta.map((m) =>
          match(m)
            .with({ kind: 'text' }, (matched) => (
              <MetaItem
                key={`${event.id}-${matched.label}`}
                label={matched.label}
                value={matched.value}
              />
            ))
            .with({ kind: 'money' }, (matched) => (
              <MetaItem
                key={`${event.id}-${matched.label}`}
                label={matched.label}
                value={formatter.currency(matched.amount, currencyCode)}
              />
            ))
            .with({ kind: 'date' }, (matched) => (
              <MetaItem
                key={`${event.id}-${matched.label}`}
                label={matched.label}
                value={<RelativeDateTime dateTime={matched.date} full />}
              />
            ))
            .with({ kind: 'link' }, (matched) => (
              <MetaItem
                key={`${event.id}-${matched.label}`}
                label={matched.label}
                value={
                  <Action
                    to={matched.url}
                    target="_blank"
                    rel="noreferrer"
                    variant="link"
                    icon="externalLink"
                    side="right"
                  >
                    {matched.urlLabel}
                  </Action>
                }
              />
            ))
            .exhaustive(),
        )}
      </dl>
    </Disclosure>
  );
}

export function Timeline<T>({
  addEvent,
  events,
  isPending,
  inputId,
  alert,
}: {
  events: CrewMerchantUi.Timeline;
  addEvent: (event: CrewMerchantUi.TimelineEvent) => Promise<T>;
  isPending: boolean;
  inputId: string;
  alert?: React.ReactNode;
}) {
  const user = useUser();

  const [comment, setComment] = useState('');
  return (
    <Card>
      {alert}

      <div className="flex items-center gap-2">
        <Icon icon="customer" className="h-7 w-7 text-neutral-300" />
        <form
          className="flex-auto"
          onSubmit={(e) => {
            e.preventDefault();
            addEvent({
              id: crypto.randomUUID(),
              type: 'comment',
              message: comment,
              datetime: new Date().toISOString(),
              subject: `${user.firstName} ${user.lastName}`.trim(),
            })
              .then(() => setComment(''))
              .catch((error) => console.error(error));
          }}
        >
          <TextInput
            id={inputId}
            label="Timeline Comment"
            labelVisuallyHidden
            placeholder="Add a comment..."
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            addon={{
              outsideEnd: (
                <Action
                  icon="add"
                  type="submit"
                  accessibilityLabel="Add Comment"
                  disabled={!comment || isPending}
                  loading={isPending}
                />
              ),
            }}
          />
        </form>
      </div>
      <Feed
        events={events}
        renderIcon={(event) => timelineEventIcons[event.type]}
      >
        {(event) => {
          if (event.type === 'comment') {
            return (
              <div className="flex-auto text-xs">
                <div className="flex items-center justify-between">
                  {event.subject && (
                    <p>
                      <span className="font-medium text-neutral-800">
                        {event.subject}
                      </span>{' '}
                      <span className="text-neutral-500">commented</span>
                    </p>
                  )}
                  <RelativeDateTime
                    dateTime={event.datetime}
                    className="flex-none text-xs text-neutral-500"
                  />
                </div>
                <p className="text-neutral-800">{event.message}</p>
              </div>
            );
          }

          return event.meta && event.meta.length > 0 ? (
            <EventContentWithMeta event={event} meta={event.meta} />
          ) : (
            <EventContent event={event} />
          );
        }}
      </Feed>
    </Card>
  );
}
